export const ramadanAdminNoteStatuses = ["active", "archived"] as const;
export type RamadanAdminNoteStatus = (typeof ramadanAdminNoteStatuses)[number];

export const ramadanAdminNoteCategories = [
  "general",
  "sponsor",
  "operations",
  "financeReference",
  "facilities",
  "followUp",
] as const;
export type RamadanAdminNoteCategory = (typeof ramadanAdminNoteCategories)[number];

export type RamadanAdminNoteMigrationMetadata = {
  legacyAuthorLabel: string | null;
  legacyCreatedAt: unknown | null;
  legacyIndex: number | null;
  legacyPath: string | null;
  legacySource: "hvicc_ramadan_iftar_admin_comments" | null;
  migratedAt: unknown | null;
};

export type RamadanAdminNoteRecord = {
  archivedAt: unknown | null;
  archivedBy: string | null;
  body: string;
  bodyHash: string;
  category: RamadanAdminNoteCategory;
  createdAt: unknown;
  createdBy: string;
  editCount: number;
  idempotencyKeyHash: string;
  iftarDayId: string | null;
  migration: RamadanAdminNoteMigrationMetadata;
  noteId: string;
  organizationId: string;
  ramadanId: string;
  source: "experience_thirty_seven";
  sponsorshipId: string | null;
  status: RamadanAdminNoteStatus;
  updatedAt: unknown;
  updatedBy: string;
};

export type RamadanAdminNoteAuditAction = "created" | "edited" | "archived";

export type RamadanAdminNoteAuditEvent = {
  action: RamadanAdminNoteAuditAction;
  actorUid: string;
  category: RamadanAdminNoteCategory;
  createdAt: unknown;
  createdBy: "system:ramadanAdminNotes";
  eventId: string;
  iftarDayId: string | null;
  nextBodyHash: string | null;
  nextStatus: RamadanAdminNoteStatus;
  noteId: string;
  organizationId: string;
  previousBodyHash: string | null;
  previousStatus: RamadanAdminNoteStatus | null;
  ramadanId: string;
  source: "experience_thirty_seven";
  sponsorshipId: string | null;
  status: "completed";
};

export const ramadanAdminNoteMaximumBodyLength = 2000;

const safeIdentifierPattern = /^[A-Za-z0-9][A-Za-z0-9_-]{1,119}$/u;
const forbiddenExecutableTextPattern = /<\s*\/?\s*script\b|javascript\s*:/iu;

export function organizationRamadanAdminNoteDocumentPath(
  organizationId: string,
  ramadanId: string,
  noteId: string,
): string {
  return `organizations/${organizationId}/ramadan/${ramadanId}/adminNotes/${noteId}`;
}

export function organizationRamadanAdminNotesCollectionPath(
  organizationId: string,
  ramadanId: string,
): string {
  return `organizations/${organizationId}/ramadan/${ramadanId}/adminNotes`;
}

export function organizationRamadanAdminNoteAuditEventDocumentPath(
  organizationId: string,
  ramadanId: string,
  eventId: string,
): string {
  return `organizations/${organizationId}/ramadan/${ramadanId}/adminNoteAuditEvents/${eventId}`;
}

export function isRamadanAdminNoteStatus(value: unknown): value is RamadanAdminNoteStatus {
  return (
    typeof value === "string" && ramadanAdminNoteStatuses.includes(value as RamadanAdminNoteStatus)
  );
}

export function isRamadanAdminNoteCategory(value: unknown): value is RamadanAdminNoteCategory {
  return (
    typeof value === "string" &&
    ramadanAdminNoteCategories.includes(value as RamadanAdminNoteCategory)
  );
}

export function normalizeRamadanAdminNoteBody(value: unknown): string | undefined {
  if (typeof value !== "string") {
    return undefined;
  }

  const normalized = value
    .replace(/\r\n?/gu, "\n")
    .replace(/\t/gu, " ")
    .split("\n")
    .map((line) => line.trimEnd())
    .join("\n")
    .trim();

  if (
    normalized.length === 0 ||
    normalized.length > ramadanAdminNoteMaximumBodyLength ||
    forbiddenExecutableTextPattern.test(normalized) ||
    hasUnsupportedControlCharacter(normalized)
  ) {
    return undefined;
  }

  return normalized;
}

function hasUnsupportedControlCharacter(value: string): boolean {
  for (const character of value) {
    const codePoint = character.charCodeAt(0);
    if (
      codePoint <= 8 ||
      codePoint === 11 ||
      codePoint === 12 ||
      (codePoint >= 14 && codePoint <= 31) ||
      codePoint === 127
    ) {
      return true;
    }
  }

  return false;
}

export function buildEmptyRamadanAdminNoteMigrationMetadata(): RamadanAdminNoteMigrationMetadata {
  return {
    legacyAuthorLabel: null,
    legacyCreatedAt: null,
    legacyIndex: null,
    legacyPath: null,
    legacySource: null,
    migratedAt: null,
  };
}

export function buildRamadanAdminNoteMigrationMetadataFromLegacyComment(input: {
  legacyAuthorLabel?: string | null;
  legacyCreatedAt?: unknown | null;
  legacyIndex: number;
  legacyPath: string;
  migratedAt: unknown;
}): RamadanAdminNoteMigrationMetadata {
  const legacyAuthorLabel = input.legacyAuthorLabel?.trim() || null;
  return {
    legacyAuthorLabel,
    legacyCreatedAt: input.legacyCreatedAt ?? null,
    legacyIndex: input.legacyIndex,
    legacyPath: input.legacyPath,
    legacySource: "hvicc_ramadan_iftar_admin_comments",
    migratedAt: input.migratedAt,
  };
}

export function validateRamadanAdminNoteRecord(
  note: RamadanAdminNoteRecord,
  expected?: {
    noteId?: string;
    organizationId?: string;
    ramadanId?: string;
  },
): string[] {
  const errors: string[] = [];

  if (!isSafeNoteId(note.noteId)) {
    errors.push("Ramadan Admin Note ID must be safe and canonical.");
  }
  if (expected?.noteId !== undefined && note.noteId !== expected.noteId) {
    errors.push("Ramadan Admin Note ID must match its path.");
  }
  if (!isSafeIdentifier(note.organizationId)) {
    errors.push("Ramadan Admin Note organization ID must be safe.");
  }
  if (expected?.organizationId !== undefined && note.organizationId !== expected.organizationId) {
    errors.push("Ramadan Admin Note organization ID must match its path.");
  }
  if (!isSafeIdentifier(note.ramadanId)) {
    errors.push("Ramadan Admin Note Ramadan ID must be safe.");
  }
  if (expected?.ramadanId !== undefined && note.ramadanId !== expected.ramadanId) {
    errors.push("Ramadan Admin Note Ramadan ID must match its path.");
  }
  if (note.iftarDayId !== null && !isSafeIftarDayId(note.iftarDayId)) {
    errors.push("Ramadan Admin Note Iftar Day reference must be safe.");
  }
  if (note.sponsorshipId !== null && !isSafeSponsorshipId(note.sponsorshipId)) {
    errors.push("Ramadan Admin Note sponsorship reference must be safe.");
  }
  if (note.sponsorshipId !== null && note.iftarDayId === null) {
    errors.push("Ramadan Admin Note sponsorship references must include an Iftar Day.");
  }
  if (!isRamadanAdminNoteCategory(note.category)) {
    errors.push("Ramadan Admin Note category must be canonical.");
  }
  if (normalizeRamadanAdminNoteBody(note.body) !== note.body) {
    errors.push("Ramadan Admin Note body must be normalized plain text.");
  }
  if (!isSafeHash(note.bodyHash)) {
    errors.push("Ramadan Admin Note body hash must be safe.");
  }
  if (!isSafeHash(note.idempotencyKeyHash)) {
    errors.push("Ramadan Admin Note idempotency hash must be safe.");
  }
  if (!isRamadanAdminNoteStatus(note.status)) {
    errors.push("Ramadan Admin Note status must be canonical.");
  }
  if (!isSafeIdentifier(note.createdBy) || !isSafeIdentifier(note.updatedBy)) {
    errors.push("Ramadan Admin Note author fields must be safe.");
  }
  if (!Number.isSafeInteger(note.editCount) || note.editCount < 0) {
    errors.push("Ramadan Admin Note edit count must be a safe non-negative integer.");
  }
  if (note.status === "active" && (note.archivedAt !== null || note.archivedBy !== null)) {
    errors.push("Active Ramadan Admin Notes must not carry archive metadata.");
  }
  if (note.status === "archived" && !isSafeIdentifier(note.archivedBy)) {
    errors.push("Archived Ramadan Admin Notes must record the archiving actor.");
  }
  if (note.source !== "experience_thirty_seven") {
    errors.push("Ramadan Admin Note source must be experience_thirty_seven.");
  }
  if (!isValidMigrationMetadata(note.migration)) {
    errors.push("Ramadan Admin Note migration metadata must be explicit and safe.");
  }

  return errors;
}

export function validateRamadanAdminNoteAuditEvent(
  event: RamadanAdminNoteAuditEvent,
  expected?: {
    eventId?: string;
    noteId?: string;
    organizationId?: string;
    ramadanId?: string;
  },
): string[] {
  const errors: string[] = [];

  if (!isSafeIdentifier(event.eventId)) {
    errors.push("Ramadan Admin Note audit event ID must be safe.");
  }
  if (expected?.eventId !== undefined && event.eventId !== expected.eventId) {
    errors.push("Ramadan Admin Note audit event ID must match its path.");
  }
  if (!isSafeNoteId(event.noteId)) {
    errors.push("Ramadan Admin Note audit note ID must be safe.");
  }
  if (expected?.noteId !== undefined && event.noteId !== expected.noteId) {
    errors.push("Ramadan Admin Note audit note ID must match.");
  }
  if (!isSafeIdentifier(event.organizationId)) {
    errors.push("Ramadan Admin Note audit organization ID must be safe.");
  }
  if (expected?.organizationId !== undefined && event.organizationId !== expected.organizationId) {
    errors.push("Ramadan Admin Note audit organization ID must match its path.");
  }
  if (!isSafeIdentifier(event.ramadanId)) {
    errors.push("Ramadan Admin Note audit Ramadan ID must be safe.");
  }
  if (expected?.ramadanId !== undefined && event.ramadanId !== expected.ramadanId) {
    errors.push("Ramadan Admin Note audit Ramadan ID must match its path.");
  }
  if (event.iftarDayId !== null && !isSafeIftarDayId(event.iftarDayId)) {
    errors.push("Ramadan Admin Note audit Iftar Day reference must be safe.");
  }
  if (event.sponsorshipId !== null && !isSafeSponsorshipId(event.sponsorshipId)) {
    errors.push("Ramadan Admin Note audit sponsorship reference must be safe.");
  }
  if (event.sponsorshipId !== null && event.iftarDayId === null) {
    errors.push("Ramadan Admin Note audit sponsorship references must include an Iftar Day.");
  }
  if (!isRamadanAdminNoteCategory(event.category)) {
    errors.push("Ramadan Admin Note audit category must be canonical.");
  }
  if (
    event.previousBodyHash !== null &&
    (!isSafeHash(event.previousBodyHash) || event.previousStatus === null)
  ) {
    errors.push("Ramadan Admin Note audit previous state must be safe.");
  }
  if (event.nextBodyHash !== null && !isSafeHash(event.nextBodyHash)) {
    errors.push("Ramadan Admin Note audit next body hash must be safe.");
  }
  if (!isRamadanAdminNoteStatus(event.nextStatus)) {
    errors.push("Ramadan Admin Note audit next status must be canonical.");
  }
  if (event.previousStatus !== null && !isRamadanAdminNoteStatus(event.previousStatus)) {
    errors.push("Ramadan Admin Note audit previous status must be canonical.");
  }
  if (event.action !== "created" && event.action !== "edited" && event.action !== "archived") {
    errors.push("Ramadan Admin Note audit action must be canonical.");
  }
  if (!isSafeIdentifier(event.actorUid)) {
    errors.push("Ramadan Admin Note audit actor must be safe.");
  }
  if (event.createdBy !== "system:ramadanAdminNotes") {
    errors.push("Ramadan Admin Note audit creator must be canonical.");
  }
  if (event.source !== "experience_thirty_seven" || event.status !== "completed") {
    errors.push("Ramadan Admin Note audit source/status must be canonical.");
  }

  return errors;
}

function isSafeIdentifier(value: unknown): value is string {
  return typeof value === "string" && safeIdentifierPattern.test(value);
}

function isSafeNoteId(value: unknown): value is string {
  return isSafeIdentifier(value) && value.startsWith("radnote_");
}

function isSafeIftarDayId(value: unknown): value is string {
  return isSafeIdentifier(value) && value.startsWith("iftar-");
}

function isSafeSponsorshipId(value: unknown): value is string {
  return isSafeIdentifier(value) && value.startsWith("iftarsp_");
}

function isSafeHash(value: unknown): value is string {
  return typeof value === "string" && /^[a-f0-9]{16,128}$/u.test(value);
}

function isValidMigrationMetadata(value: unknown): value is RamadanAdminNoteMigrationMetadata {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return false;
  }
  const migration = value as RamadanAdminNoteMigrationMetadata;
  return (
    (migration.legacySource === null ||
      migration.legacySource === "hvicc_ramadan_iftar_admin_comments") &&
    (migration.legacyPath === null ||
      (typeof migration.legacyPath === "string" && migration.legacyPath.length <= 240)) &&
    (migration.legacyAuthorLabel === null ||
      (typeof migration.legacyAuthorLabel === "string" &&
        migration.legacyAuthorLabel.length <= 120)) &&
    (migration.legacyIndex === null ||
      (Number.isSafeInteger(migration.legacyIndex) && migration.legacyIndex >= 0))
  );
}
